import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

export default function FinancePaymentDialog({ clients, invoices, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_id: clients[0]?.client_id || '',
    invoice_id: '',
    amount: 0,
    payment_date: today(),
    method: 'virement',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Show only unpaid invoices for selected client
  const clientInvoices = useMemo(() =>
    invoices.filter(i => i.client_id === form.client_id && i.status !== 'paid'),
    [invoices, form.client_id]
  );

  const save = async () => {
    if (!form.client_id) return toast.error("Sélectionnez un client");
    if (!form.amount || form.amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.invoice_id) delete payload.invoice_id;
      await axios.post(`${API}/finance/payments`, payload);
      toast.success("Encaissement enregistré");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="finance-payment-dialog">
        <DialogHeader><DialogTitle>Enregistrer une rentrée bancaire</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-neutral-500">Client</Label>
            <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
              <SelectTrigger data-testid="pay-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {clientInvoices.length > 0 && (
            <div>
              <Label className="text-xs text-neutral-500">Lier à une facture (optionnel)</Label>
              <Select value={form.invoice_id} onValueChange={(v) => set('invoice_id', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="pay-invoice"><SelectValue placeholder="Aucune (rentrée libre)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucune (rentrée libre)</SelectItem>
                  {clientInvoices.map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.month} · {i.label || i.type} · {i.amount.toFixed(0)}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">Si total payé ≥ montant facture, elle passe automatiquement en « Payée ».</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-neutral-500">Date d'encaissement</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} data-testid="pay-date" />
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Méthode</Label>
              <Select value={form.method} onValueChange={(v) => set('method', v)}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="cb">Carte bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="prelevement">Prélèvement</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Montant encaissé</Label>
            <div className="relative">
              <Input type="number" min="0" step="10" value={form.amount} onChange={(e) => set('amount', parseFloat(e.target.value) || 0)} className="pr-8" data-testid="pay-amount" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">€</span>
            </div>
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Notes</Label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Ref virement, commentaire…" data-testid="pay-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pay-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
